import React, { useState, useMemo } from "react";
import { contabilidadData, CATEGORIA_LABELS, getDocHref } from "../data/contabilidadData";
import "../styles/contabilidad.css";

const CATEGORIA_KEYS = Object.keys(CATEGORIA_LABELS);
const TRIMESTRES = ["1T", "2T", "3T", "4T"];

function getYears() {
  return Object.keys(contabilidadData).sort((a, b) => b - a);
}

function getTrimestreData(year, trimestre) {
  const ejercicio = contabilidadData[year];
  if (!ejercicio) return null;
  return ejercicio.trimestres[trimestre] || null;
}

function getTrimestresDisponibles(year) {
  const ejercicio = contabilidadData[year];
  return ejercicio ? Object.keys(ejercicio.trimestres) : [];
}

function getCategoriasDisponibles(year, trimestre) {
  const t = getTrimestreData(year, trimestre);
  return t ? Object.keys(t.categorias) : [];
}

function getDocumentos(year, trimestre, categoria) {
  const t = getTrimestreData(year, trimestre);
  return (t && t.categorias[categoria]) || [];
}

export default function Contabilidad() {
  const years = useMemo(getYears, []);
  const [year, setYear] = useState(years[0]);
  const [trimestre, setTrimestre] = useState(getTrimestresDisponibles(years[0])[0]);
  const [categoria, setCategoria] = useState(
    getCategoriasDisponibles(years[0], getTrimestresDisponibles(years[0])[0])[0]
  );
  const [query, setQuery] = useState("");

  const handleYearChange = (y) => {
    const t = getTrimestresDisponibles(y)[0];
    setYear(y);
    setTrimestre(t);
    setCategoria(getCategoriasDisponibles(y, t)[0]);
    setQuery("");
  };

  const handleTrimestreChange = (t) => {
    setTrimestre(t);
    setCategoria(getCategoriasDisponibles(year, t)[0]);
    setQuery("");
  };

  const resultadosBusqueda = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    const resultados = [];
    years.forEach((y) => {
      Object.entries(contabilidadData[y].trimestres).forEach(([t, trimestreData]) => {
        Object.entries(trimestreData.categorias).forEach(([catKey, docs]) => {
          docs.forEach((doc) => {
            if (doc.label.toLowerCase().includes(q)) {
              resultados.push(Object.assign({}, doc, { year: y, trimestre: t, categoria: catKey }));
            }
          });
        });
      });
    });
    return resultados;
  }, [query, years]);

  const trimestresDisponibles = getTrimestresDisponibles(year);
  const categoriasDisponibles = getCategoriasDisponibles(year, trimestre);
  const documentos = getDocumentos(year, trimestre, categoria);

  return (
  <div id="main" className="wrapper">

    <div className="container">

      <header className="major">
        <h2>Contabilidad Gubernamental</h2>
        <p>
          Consulta la información financiera por ejercicio,
          trimestre y categoría.
        </p>
      </header>

      <div className="contabilidad">

        <div className="contabilidad__search">
          <input
            type="text"
            placeholder="Buscar documento, ej. Estado de Situación Financiera"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

      {resultadosBusqueda ? (
        <ResultadosBusqueda query={query} resultados={resultadosBusqueda} />
      ) : (
        <React.Fragment>
          <div className="contabilidad__years">
            {years.map((y) => (
              <button
                key={y}
                type="button"
                className={"contabilidad__pill" + (y === year ? " contabilidad__pill--active" : "")}
                onClick={() => handleYearChange(y)}
              >
                {y}
              </button>
            ))}
          </div>

          <div className="contabilidad__trimestres">
            {TRIMESTRES.map((t) => {
              const disponible = trimestresDisponibles.indexOf(t) !== -1;
              return (
                <button
                  key={t}
                  type="button"
                  disabled={!disponible}
                  className={
                    "contabilidad__trimestre-btn" + (t === trimestre ? " contabilidad__trimestre-btn--active" : "")
                  }
                  onClick={() => handleTrimestreChange(t)}
                >
                  {t}
                </button>
              );
            })}
          </div>

          <div className="contabilidad__layout">
            <div className="contabilidad__categorias">
              <div className="contabilidad__cat-list">
                {CATEGORIA_KEYS.map((catKey) => {
                  const disponible = categoriasDisponibles.indexOf(catKey) !== -1;
                  const count = disponible ? getDocumentos(year, trimestre, catKey).length : 0;
                  return (
                    <button
                      key={catKey}
                      type="button"
                      disabled={!disponible}
                      className={
                        "contabilidad__cat-item" + (catKey === categoria ? " contabilidad__cat-item--active" : "")
                      }
                      onClick={() => {
                        setCategoria(catKey);
                        setQuery("");
                      }}
                    >
                      <span>{CATEGORIA_LABELS[catKey]}</span>
                      {disponible && <span className="contabilidad__cat-badge">{count}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="contabilidad__content">
              <p className="contabilidad__breadcrumb">
                {year} · {trimestre} · {CATEGORIA_LABELS[categoria]}
              </p>
              <DocumentList year={year} trimestre={trimestre} categoriaKey={categoria} documentos={documentos} />
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
    </div>
    </div>
  );
}

function DocumentList({ year, trimestre, categoriaKey, documentos }) {
  if (!documentos.length) {
    return <p className="contabilidad__breadcrumb">Sin documentos publicados.</p>;
  }
  return (
    <div className="contabilidad__docs">
      {documentos.map((doc) => {
        const href = getDocHref(year, trimestre, categoriaKey, doc);
        return href ? (
          <a key={doc.file || doc.label} href={href} target="_blank" rel="noopener noreferrer" className="contabilidad__doc">
            {doc.label}
          </a>
        ) : (
          <span key={doc.file || doc.label} className="contabilidad__doc contabilidad__doc--disabled">
            {doc.label} — No disponible
          </span>
        );
      })}
    </div>
  );
}

function ResultadosBusqueda({ query, resultados }) {
  return (
    <div>
      <p className="contabilidad__breadcrumb">Resultados para "{query}"</p>
      {resultados.length === 0 ? (
        <p className="contabilidad__breadcrumb">Sin coincidencias.</p>
      ) : (
        <div className="contabilidad__docs">
          {resultados.map((doc) => {
            const href = getDocHref(doc.year, doc.trimestre, doc.categoria, doc);
            const key = doc.year + "-" + doc.trimestre + "-" + doc.categoria + "-" + (doc.file || doc.label);
            return href ? (
              <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="contabilidad__doc">
                {doc.label}
                <small>
                  {doc.year} · {doc.trimestre} · {CATEGORIA_LABELS[doc.categoria]}
                </small>
              </a>
            ) : (
              <span key={key} className="contabilidad__doc contabilidad__doc--disabled">
                {doc.label} — No disponible
                <small>
                  {doc.year} · {doc.trimestre} · {CATEGORIA_LABELS[doc.categoria]}
                </small>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}